import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { client } from '../../utils/client'
import { OrderStatus } from 'src/utils/enum'

// Actions - used with useDispatch
export const reportSlice = createSlice({
  name: 'report',
  initialState: {
    summary: null,
    totalSummary: null,
    report: null,
    getRevenueSummary: {
      status: null,
      error: null,
    },
    getTotalSummary: {
      status: null,
      error: null,
    },
    getRevenueReport: {
      status: null,
      error: null,
    },
    createReportPDF: {
      status: null,
      error: null,
    },
  },
  reducers: {
    clearStatus: (state) => {
      for (const key in state) {
        // Clear status and error
        if (state[key]?.status) {
          state[key].status = null
        }
        if (state[key]?.error) {
          state[key].error = null
        }
      }
    },
    clearData: (state) => {
      // Clear actual data (summary and totalSummary)
      state.summary = null
      state.totalSummary = null
      state.report = null
    },
  },
  extraReducers: (builder) => {
    builder
      // getRevenueSummary
      .addCase(getRevenueSummary.pending, (state, action) => {
        state.getRevenueSummary.status = 'loading'
      })
      .addCase(getRevenueSummary.fulfilled, (state, action) => {
        state.getRevenueSummary.status = 'succeeded'
        state.getRevenueSummary.error = null // Clear any previous errors

        // Store getRevenueSummary info
        if (action.payload.data.success) {
          state.summary = action.payload.data.data
        }
      })
      .addCase(getRevenueSummary.rejected, (state, action) => {
        state.getRevenueSummary.status = 'failed'
        state.getRevenueSummary.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear summary info
        state.summary = null
      })
      // getTotalSummary
      .addCase(getTotalSummary.pending, (state, action) => {
        state.getTotalSummary.status = 'loading'
      })
      .addCase(getTotalSummary.fulfilled, (state, action) => {
        state.getTotalSummary.status = 'succeeded'
        state.getTotalSummary.error = null // Clear any previous errors

        // Store getTotalSummary info
        if (action.payload.data.success) {
          state.totalSummary = action.payload.data.data
        }
      })
      .addCase(getTotalSummary.rejected, (state, action) => {
        state.getTotalSummary.status = 'failed'
        state.getTotalSummary.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear totalSummary info
        state.totalSummary = null
      })
      // getRevenueReport
      .addCase(getRevenueReport.pending, (state, action) => {
        state.getRevenueReport.status = 'loading'
      })
      .addCase(getRevenueReport.fulfilled, (state, action) => {
        state.getRevenueReport.status = 'succeeded'
        state.getRevenueReport.error = null // Clear any previous errors

        // Store getRevenueReport info
        if (action.payload.data.success) {
          state.report = action.payload.data.data
        }
      })
      .addCase(getRevenueReport.rejected, (state, action) => {
        state.getRevenueReport.status = 'failed'
        state.getRevenueReport.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear summary info
        state.report = null
      })
      // createReportPDF
      .addCase(createReportPDF.pending, (state, action) => {
        state.createReportPDF.status = 'loading'
      })
      .addCase(createReportPDF.fulfilled, (state, action) => {
        state.createReportPDF.status = 'succeeded'
        state.createReportPDF.error = null // Clear any previous errors

        // Store createReportPDF info, if available
      })
      .addCase(createReportPDF.rejected, (state, action) => {
        state.createReportPDF.status = 'failed'
        state.createReportPDF.error = {
          status: action.payload.status,
          message: action.payload.message, // Set the error message
        }

        // Clear reportPdf info, if needed
      })
  },
})
export const { clearStatus, clearData } = reportSlice.actions

export const getRevenueSummary = createAsyncThunk(
  'report/getRevenueSummary',
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const branchCode = state.branch?.currentSelectedBranchCode
      const validBranchCode =
        branchCode && String(branchCode).trim() && String(branchCode).trim() !== 'null'
      if (!validBranchCode) {
        return rejectWithValue({
          status: 400,
          message: 'กรุณาเพิ่มหรือเลือกสาขาก่อนดูรายงาน',
        })
      }
      const queryParams = new URLSearchParams()
      queryParams.set('marketCategory', 'Laundry')
      queryParams.set('startOfPeriod', params.startOfPeriod || '2024-01-01')
      queryParams.set('endOfPeriod', params.endOfPeriod || '2024-12-31')
      queryParams.set('branchCode', branchCode)
      const queryString = queryParams.toString()

      const response = await client.get(`/shop/report/summary?${queryString}`)
      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getTotalSummary = createAsyncThunk(
  'report/getTotalSummary',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const branchCode = state.branch?.currentSelectedBranchCode
      const validBranchCode =
        branchCode && String(branchCode).trim() && String(branchCode).trim() !== 'null'
      if (!validBranchCode) {
        return rejectWithValue({
          status: 400,
          message: 'กรุณาเพิ่มหรือเลือกสาขาก่อนดูรายงาน',
        })
      }
      const queryParams = new URLSearchParams()
      queryParams.set('marketCategory', 'Laundry')
      queryParams.set('branchCode', branchCode)
      const queryString = queryParams.toString()

      const response = await client.get(`/shop/report/summary?${queryString}`)
      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const getRevenueReport = createAsyncThunk(
  'report/getRevenueReport',
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const branchCode = state.branch?.currentSelectedBranchCode
      const validBranchCode =
        branchCode && String(branchCode).trim() && String(branchCode).trim() !== 'null'
      if (!validBranchCode) {
        return rejectWithValue({
          status: 400,
          message: 'กรุณาเพิ่มหรือเลือกสาขาก่อนดูรายงาน',
        })
      }
      const queryParams = new URLSearchParams()
      queryParams.set('marketCategory', 'Laundry')
      queryParams.set('startOfPeriod', params.startOfPeriod || '2024-01-01')
      queryParams.set('endOfPeriod', params.endOfPeriod || '2024-12-31')
      queryParams.set('branchCode', branchCode)
      queryParams.set('page', params.page || 1)
      queryParams.set('limit', params.limit || 10)
      const queryString = queryParams.toString()

      const response = await client.get(`/shop/report/details?${queryString}`)
      return response
    } catch (error) {
      // Handle the error gracefully and return it as part of the payload
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      // API response error
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

export const createReportPDF = createAsyncThunk(
  'report/createReportPDF',
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const branchCode = state.branch?.currentSelectedBranchCode
      const validBranchCode =
        branchCode && String(branchCode).trim() && String(branchCode).trim() !== 'null'
      if (!validBranchCode) {
        return rejectWithValue({
          status: 400,
          message: 'กรุณาเพิ่มหรือเลือกสาขาก่อนดูรายงาน',
        })
      }
      const queryParams = new URLSearchParams()
      queryParams.set('marketCategory', 'Laundry')
      queryParams.set('startOfPeriod', params.startOfPeriod || '2024-01-01')
      queryParams.set('endOfPeriod', params.endOfPeriod || '2024-12-31')
      queryParams.set('branchCode', branchCode)
      queryParams.set('issueFor', 'Shop')
      queryParams.set('reportType', params.reportType)
      const queryString = queryParams.toString()

      const response = await client.get(`/shop/report/details/create-pdf?${queryString}`)
      return response
    } catch (error) {
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
        return rejectWithValue({
          status: 503,
          message: error.message,
        })
      }
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || error.message,
      })
    }
  },
)

// Selector (Read data) - used with useSelector
export const selectSummary = (state) => state.report.summary
export const selectGetSummaryStatus = (state) => state.report.getRevenueSummary.status
export const selectGetSummaryError = (state) => state.report.getRevenueSummary.error

export const selectTotalSummary = (state) => state.report.totalSummary
export const selectGetTotalSummaryStatus = (state) => state.report.getTotalSummary.status
export const selectGetTotalSummaryError = (state) => state.report.getTotalSummary.error

export const selectRevenue = (state) => state.report.report
export const selectGetReportStatus = (state) => state.report.getRevenueReport.status
export const selectGetReportError = (state) => state.report.getRevenueReport.error

export const selectCreateReportPDFStatus = (state) => state.report.createReportPDF.status
export const selectCreateReportPDFError = (state) => state.report.createReportPDF.error

export default reportSlice.reducer
